/**
 * Viewport mode: which studio shell to render (mobile vs desktop).
 *
 * The studio renders two different shells (components/StudioMobile.vue and
 * components/NewStudio/Shell.vue). Picking between them on the client alone
 * means the server has no way to know which to send, so it always sends the
 * desktop shell and the phone swaps to the mobile one after hydration —
 * a visible flash of the desktop UI on every mobile load.
 *
 * This composable resolves the viewport during SSR (from the request's
 * User-Agent header) so the correct shell is in the first HTML response.
 *
 * The server-side check is a User-Agent sniff rather than a true width
 * measurement — the real width is unknown until the client connects — so
 * the client applies the SAME predicate afterwards: a phone-like UA is
 * mobile regardless of orientation, and anything narrower than the
 * breakpoint is mobile too. Both sides agreeing is what keeps the swap
 * from flashing.
 */

/** User-Agent substrings that indicate a phone. */
const MOBILE_UA_PATTERNS = [
  'iphone',
  'ipod',
  'windows phone',
  'blackberry',
  'bb10',
  'opera mini',
  'opera mobi',
  'iemobile',
]

/**
 * Tablets are excluded: they are always at least the 768px breakpoint, so
 * the width check would never pick mobile for them, and treating them as
 * mobile here would just create a flash in the other direction.
 */
const TABLET_UA_PATTERNS = ['ipad', 'tablet', 'kindle', 'silk']

/**
 * True when the User-Agent looks like a phone rather than a tablet or
 * desktop.
 */
export const isMobileUserAgent = (userAgent: string | null | undefined): boolean => {
  if (!userAgent) return false
  const ua = userAgent.toLowerCase()
  if (TABLET_UA_PATTERNS.some((pattern) => ua.includes(pattern))) return false
  // Android phones carry "Mobile"; Android tablets do not.
  if (ua.includes('android') && !ua.includes('mobile')) return false
  if (MOBILE_UA_PATTERNS.some((pattern) => ua.includes(pattern))) return true
  // Any other UA that advertises a mobile browser counts too (covers
  // Android phones whose UA has no other phone marker).
  return ua.includes('mobile')
}

/** The same 768px breakpoint the client-side check uses. */
export const MOBILE_BREAKPOINT = 768

export const useViewportMode = () => {
  // useState carries the server's answer across to the client in the Nuxt
  // payload. Without it the client would start from `false` (import.meta.server
  // is false there) and render the desktop shell during hydration, which
  // mismatches the server-rendered mobile shell.
  const isMobileViewport = useState<boolean>('studio-is-mobile-viewport', () => {
    if (!import.meta.server) return false
    const requestHeaders = useRequestHeaders(['user-agent'])
    return isMobileUserAgent(requestHeaders['user-agent'])
  })

  const updateViewport = () => {
    if (!process.client) return
    // Same predicate the server used, plus the width check the mobile shell
    // has always used. A phone stays on the mobile shell in landscape; a
    // narrow desktop window still switches to mobile.
    isMobileViewport.value = isMobileUserAgent(navigator.userAgent)
      || window.innerWidth < MOBILE_BREAKPOINT
  }

  onMounted(() => {
    updateViewport()
    window.addEventListener('resize', updateViewport)
  })

  onUnmounted(() => {
    if (process.client) window.removeEventListener('resize', updateViewport)
  })

  return { isMobileViewport }
}
